// Fronted/src/components/admin/homescreens/WelcomeSection.jsx
import { useEffect, useState } from "react";
import { FaAngleRight, FaMicrophone } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { useAnnouncements } from '../../contexts/AnnouncementsContext';
import { useEvents } from '../../contexts/EventsContext';
import PlaceholderImage from "../../images/7.jpg";


const WelcomeSection = () => {
  // State for social media posts
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'sermons', 'events', 'announcements'
  const {events} = useEvents();
  const {announcements} = useAnnouncements();

  // Fetch social media posts
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const posts = [];
        
        if (events.length > 0) {
          // Create a copy of each event and add the type property
          const eventsWithType = events.map(event => ({
            ...event,
            type: 'event'
          }));
          posts.push(...eventsWithType);
        }
        
        if (announcements.length > 0) {
          // Create a copy of each announcement and add the type property
          const announcementsWithType = announcements.map(announcement => ({
            ...announcement,
            type: 'announcement'
          }));
          posts.push(...announcementsWithType);
        }
        
        setPosts(posts);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching posts:", error);
        setLoading(false);
      }
    };
  
    fetchPosts();
  }, [events, announcements]);

  // Format date for social media posts
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return new Date();
    if (timestamp._seconds && typeof timestamp._seconds === 'number') {
      const milliseconds = timestamp._seconds * 1000 + Math.floor(timestamp._nanoseconds / 1000000);
      return new Date(milliseconds);
    }
    return new Date(timestamp); // Fallback for string or Date object
  };

  const formatEventDate = (startDate, endDate, isSpanningEvent) => {
    if (!startDate) return "Date not specified";
    try {
      const start = parseTimestamp(startDate);
      if (isNaN(start.getTime())) {
        console.warn("Invalid start date:", startDate, "Falling back to default");
        return "Date not specified";
      }
      if (isSpanningEvent && endDate) {
        const end = parseTimestamp(endDate);
        if (isNaN(end.getTime())) {
          console.warn("Invalid end date:", endDate, "Using start date only");
          return Intl.DateTimeFormat('en-ZA', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'Africa/Johannesburg',
          }).format(start);
        }
        return `${Intl.DateTimeFormat('en-ZA', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'Africa/Johannesburg',
        }).format(start)} - ${Intl.DateTimeFormat('en-ZA', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'Africa/Johannesburg',
        }).format(end)}`;
      }
      return Intl.DateTimeFormat('en-ZA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'Africa/Johannesburg',
      }).format(start);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date not specified";
    }
  };
 // Filter posts based on active tab
  const filteredPosts = activeTab === 'all' 
    ? posts 
    : posts.filter(post => post.type === activeTab);

  const uniqueCategories = [...new Set(posts.map(item => item.type))];

  return (
    <div className="w-full px-4 md:px-0 md:w-1/2">
      {/* Welcome and Announcements buttons */}
      <div className="flex flex-col space-y-4 mb-6">
        <Link to="./welcome" className="flex items-center justify-between p-4 bg-white rounded-lg shadow-lg">
          <p className="font-medium text-gray-800">Welcome to church</p>
          <FaAngleRight className="text-gray-600" />
        </Link>

        <Link to="/events/?category=announcements" className="flex items-center justify-between p-4 bg-white rounded-lg shadow-lg">
          <div className="flex items-center">
            <FaMicrophone className="mr-2 text-gray-600" />
            <p className="font-medium text-gray-800">Announcements</p>
          </div>
          <FaAngleRight className="text-gray-600" />
        </Link>
      </div>

      {/* Social Media Posts Section */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Recent Updates</h2>
          
          {/* Tabs for filtering content - scrollable on mobile */}
          <div className="flex overflow-x-auto pb-2 mt-4 hide-scrollbar">
            <div className="flex space-x-2 min-w-max">
              {
                uniqueCategories.length > 1 ? (uniqueCategories.map((category) => (<button
                  onClick={() => setActiveTab(category)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    activeTab === category
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>))
                ) : 
                (<button
                onClick={() => setActiveTab('all')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  activeTab === 'all'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>)
              }
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredPosts.length > 0 ? (
              filteredPosts.map((post) => (
                <div key={post.id} className="p-4 ">
                  {/* Post Header */}
                  <div className="flex items-center mb-3">
                    <img 
                      src={PlaceholderImage} 
                      alt="Church Logo" 
                      className="w-8 h-8 rounded-full mr-3"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">Excellent Love Ministries</h3>
                      <p className="text-gray-500 text-xs">{formatEventDate(post.createdAt)}</p>
                    </div>
                  </div>
                  
                  {/* Post Content */}
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 mb-2">{post.title}</h4>
                    <p className="text-sm text-gray-700 mb-4">{post.description}</p>
                    
                    {/* Post Image */}
                    <div className="mb-4">
                      <img 
                        src={post.imageUrl} 
                        alt={post.title} 
                        className="w-full rounded-lg"
                      />
                    </div>
                    
                    {/* Post Type Badge and Additional Info */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${
                        post.type === 'sermon' ? 'bg-blue-100 text-blue-800' :
                        post.type === 'event' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {post.type.charAt(0).toUpperCase() + post.type.slice(1)}
                      </span>
                      
                      {/* Sermon specific info */}
                      {post.type === 'sermon' && post.duration && (
                        <span className="text-gray-500 text-xs flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {post.duration}
                        </span>
                      )}
                      
                      {/* Event specific info */}
                      {post.type === 'event' && post.location && (
                        <span className="text-gray-500 text-xs flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {post.location}
                        </span>
                      )}
                      
                      {/* Announcement specific info */}
                      {post.type === 'announcement' && post.time && (
                        <span className="text-gray-500 text-xs flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {post.time}
                        </span>
                      )}
                    </div>
                    
                    {/* Post Actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div className="flex items-center space-x-3">
                        <button className="flex items-center text-gray-500 hover:text-primary">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                          <span className="text-xs">{post.likes}</span>
                        </button>
                        <button className="flex items-center text-gray-500 hover:text-primary">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <span className="text-xs">{post.comments}</span>
                        </button>
                        {post.type === 'sermon' && post.views && (
                          <span className="flex items-center text-gray-500">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span className="text-xs"></span>
                          </span>
                        )}
                      </div>
                      <Link to={`${activeTab === 'event' ? '/events/?category=events' : '/events/?category=announcements'}`} className="text-xs text-primary hover:text-primary font-medium">
                        Read more
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <p className="text-gray-500 text-sm">No posts found in this category.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add CSS for hiding scrollbar but allowing scroll functionality */}
      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default WelcomeSection;